import { useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { TrendingUp, ArrowLeft, Search, Users2, CheckCircle2, AlertTriangle, Award, BookOpen, Clock, Mail, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";
import useAssessmentData from "../hooks/useAssessmentData";

/**
 * Student progress — the author's REAL enrolled students.
 *
 * This page rendered eight invented students ("Chidiebere Okafor, 78% progress,
 * 12-day streak, strengths/improvements") that no API produced. The API does
 * report enrolled students per course, and that is what is shown now.
 *
 * Anything the API does NOT report per student — progress percentage, average
 * score, quiz counts, streaks, "strengths" — renders as "—" instead of a
 * fabricated number. Those figures belong to the learner's own endpoints.
 *
 * Source: GET /api/resource-person/courses             → the course picker
 *         GET /api/resource-person/courses/:id/students → real enrollment
 */
const metric = (value, suffix = "") =>
  value === null || value === undefined || Number.isNaN(Number(value)) ? "—" : `${value}${suffix}`;

/** Read the first present field from a list of aliases. */
const field = (record, ...names) => {
  for (const name of names) {
    if (record[name] !== undefined && record[name] !== null && record[name] !== "") return record[name];
  }
  return null;
};

/** A short "first name" for the avatar bubble. */
const initial = (name) => String(name || "S").trim().charAt(0).toUpperCase() || "S";

/** Format an enrollment date; "—" when the API gave none. */
const formatDate = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "—"
    : parsed.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};


const avatarColors = [
  "bg-primary", "bg-success", "bg-info", "bg-warning",
  "bg-violet-500", "bg-pink-500", "bg-orange-500"
];

export default function StudentProgressPage() {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expandedStudent, setExpandedStudent] = useState(null);
  const {
    courses,
    coursesLoading,
    coursesError,
    selectedCourseId,
    setSelectedCourseId,
    courseStudents,
    courseStudentsLoading,
    courseStudentsError,
    courseStudentsTotal,
  } = useAssessmentData();

  // Real enrollment records. Per-student progress is not reported to the author,
  // so those fields stay null and render as "—" rather than being invented.
  const students = courseStudents.map((student) => {
    const name =
      field(student, "name", "fullName", "full_name") ||
      [field(student, "first_name", "firstName"), field(student, "last_name", "lastName")]
        .filter(Boolean)
        .join(" ") ||
      field(student, "email", "studentEmail") ||
      "Student";
    const progress = field(student, "progress", "progressPercentage", "completionRate", "percentComplete");
    const avgScore = field(student, "averageScore", "avgScore", "average");
    return {
      id: student.id || field(student, "studentId", "userId", "email"),
      name,
      initials: initial(name),
      email: field(student, "email", "studentEmail"),
      avatar: field(student, "avatar", "image", "photo", "picture"),
      status: field(student, "status", "state"),
      enrolledAt: field(student, "enrolledAt", "createdAt", "joinedAt"),
      // Reported by the API only if present.
      progress: progress === null ? null : Number(progress),
      avgScore: avgScore === null ? null : Number(avgScore),
    };
  });

  const filtered = students.filter((s) => {
    const status = String(s.status || "").toUpperCase();
    const matchFilter =
      filter === "all" ||
      (filter === "active" && (status === "ACTIVE" || status === "ENROLLED")) ||
      (filter === "completed" && (status === "COMPLETED" || status === "COMPLETE")) ||
      (filter === "inactive" && (status === "INACTIVE" || status === "DROPPED"));
    const matchSearch =
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      String(s.email || "").toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  // Aggregates use only figures the API actually reported.
  const withProgress = students.filter((s) => s.progress !== null && Number.isFinite(s.progress));
  const withScore = students.filter((s) => s.avgScore !== null && Number.isFinite(s.avgScore));
  const avgProgress = withProgress.length
    ? Math.round(withProgress.reduce((s, st) => s + st.progress, 0) / withProgress.length)
    : null;
  const avgScore = withScore.length
    ? Math.round(withScore.reduce((s, st) => s + st.avgScore, 0) / withScore.length)
    : null;
  const activeCount = students.filter(
    (s) => String(s.status || "").toUpperCase() === "ACTIVE" || String(s.status || "").toUpperCase() === "ENROLLED"
  ).length;

  const filters = ["all", "active", "completed", "inactive"];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-4 gradient-warning rounded-2xl shadow-lg">
            <TrendingUp className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight mb-1">Student Progress</h1>
            <p className="text-muted-foreground text-lg">Track individual student achievements and support needs</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Link to="/resource-person/assessment">
            <Button variant="outline" size="lg" className="shadow-sm font-medium">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
          </Link>
          <Button size="lg" className="gradient-warning text-white shadow-md font-semibold">
            Export Report
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Students", value: courseStudentsTotal || students.length, icon: Users2, color: "text-warning" },
          { label: "Active", value: activeCount, icon: CheckCircle2, color: "text-success" },
          { label: "Avg Score", value: metric(avgScore, avgScore === null ? "" : "%"), icon: AlertTriangle, color: "text-destructive" },
          { label: "Avg Progress", value: metric(avgProgress, avgProgress === null ? "" : "%"), icon: TrendingUp, color: "text-primary" }
        ].map((stat, i) => (
          <Card key={i} className="border-0 shadow-md">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-muted-foreground">{stat.label}</p>
                <stat.icon className={`h-5 w-5 ${stat.color}`} />
              </div>
              <p className="text-3xl font-extrabold">{stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search + Filter */}
      {/* Course picker — enrollment is per course. */}
      <div className="flex flex-col gap-2 rounded-xl border bg-muted/30 p-4 sm:flex-row sm:items-center">
        <label className="text-sm font-semibold" htmlFor="student-course">Course</label>
        <select
          id="student-course"
          className="flex-1 rounded-lg border bg-background px-3 py-2 text-sm"
          value={selectedCourseId}
          disabled={coursesLoading || !courses.length}
          onChange={(e) => setSelectedCourseId(e.target.value)}
        >
          {courses.length ? (
            courses.map((c) => (
              <option key={c.id} value={c.id}>{c.title || "Untitled course"}</option>
            ))
          ) : (
            <option value="">{coursesLoading ? "Loading courses…" : "No courses yet"}</option>
          )}
        </select>
      </div>

      {coursesError ? (
        <p className="m-0 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">{coursesError}</p>
      ) : null}
      {courseStudentsError ? (
        <p className="m-0 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">{courseStudentsError}</p>
      ) : null}

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-warning/30"
            placeholder="Search students by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          {filters.map(f => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? "default" : "outline"}
              onClick={() => setFilter(f)}
              className={filter === f ? "gradient-warning text-white" : ""}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* Student Cards */}
      <div className="grid gap-4">
        {!courseStudentsLoading && !filtered.length ? (
          <Card className="border-0 shadow-md">
            <CardContent className="p-10 text-center">
              <Users2 className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
              <p className="mb-1 font-semibold">
                {selectedCourseId ? "No students enrolled yet" : "Choose a course to see its students"}
              </p>
              <p className="m-0 text-sm text-muted-foreground">
                {selectedCourseId
                  ? "Students appear here as learners enroll in this course."
                  : "Enrolled students are listed per course."}
              </p>
            </CardContent>
          </Card>
        ) : null}
        {filtered.map((student, idx) => {
          const isExpanded = expandedStudent === student.id;
          const status = String(student.status || "").toUpperCase();
          const isActive = status === "ACTIVE" || status === "ENROLLED";
          const hasProgress = student.progress !== null && Number.isFinite(student.progress);
          const hasScore = student.avgScore !== null && Number.isFinite(student.avgScore);

          return (
            <Card key={student.id} className="border-0 shadow-md hover:shadow-lg transition-all duration-200 overflow-hidden">
              <CardContent className="p-0">
                <div
                  className="p-5 cursor-pointer"
                  onClick={() => setExpandedStudent(isExpanded ? null : student.id)}
                >
                  <div className="flex items-center gap-4">
                    {/* Avatar — a real image when the API gave one, else initials */}
                    <div className={`h-12 w-12 rounded-2xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${avatarColors[idx % avatarColors.length]}`}>
                      {student.avatar ? (
                        <img src={student.avatar} alt="" className="h-full w-full rounded-2xl object-cover" />
                      ) : (
                        student.initials
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="font-bold text-base">{student.name}</h3>
                        {student.status ? (
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                            {student.status}
                          </span>
                        ) : null}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {student.email || "No email reported"}
                      </p>
                    </div>

                    {/* Progress — rendered only when the API reported it */}
                    {hasProgress ? (
                      <div className="hidden md:block w-48 space-y-1">
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Progress</span>
                          <span className="font-bold text-foreground">{student.progress}%</span>
                        </div>
                        <Progress value={student.progress} className="h-2" />
                      </div>
                    ) : null}

                    {/* Score */}
                    <div className="hidden md:flex flex-col items-center w-20">
                      <p className="text-2xl font-extrabold">{hasScore ? `${student.avgScore}%` : "—"}</p>
                      <p className="text-xs text-muted-foreground">Avg Score</p>
                    </div>

                    <div className="text-muted-foreground">
                      {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                    </div>
                  </div>

                  {hasProgress ? (
                    <div className="md:hidden mt-3 space-y-1">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Progress</span>
                        <span className="font-bold text-foreground">{student.progress}%</span>
                      </div>
                      <Progress value={student.progress} className="h-2" />
                    </div>
                  ) : null}
                </div>

                {/* Expanded Details */}
                <div className={`transition-all duration-300 overflow-hidden ${isExpanded ? 'max-h-[600px]' : 'max-h-0'}`}>
                  <div className="px-5 pb-5 pt-0 border-t space-y-5">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4">
                      {[
                        { label: "Status", value: student.status || "—", icon: Award },
                        { label: "Enrolled", value: formatDate(student.enrolledAt), icon: Clock },
                        { label: "Avg Score", value: hasScore ? `${student.avgScore}%` : "—", icon: BookOpen },
                        { label: "Progress", value: hasProgress ? `${student.progress}%` : "—", icon: TrendingUp }
                      ].map((item, i) => (
                        <div key={i} className="bg-muted/40 rounded-xl p-3 text-center">
                          <item.icon className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                          <p className="text-sm font-bold break-words">{item.value}</p>
                          <p className="text-xs text-muted-foreground">{item.label}</p>
                        </div>
                      ))}
                    </div>

                    {/* Per-student quiz/assignment/streak detail and the strengths /
                        improvements review belonged to learner-facing endpoints that do
                        not exist for the author, so they were removed rather than filled
                        with invented text. */}
                    <p className="m-0 rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground">
                      Per-student quiz results, assignment counts and streaks are not reported
                      to the author by the API, so they are not shown here.
                    </p>

                    <div className="flex gap-2">
                      {student.email ? (
                        <a href={`mailto:${student.email}`}>
                          <Button size="sm" variant="outline" className="text-xs">
                            <Mail className="mr-1.5 h-3.5 w-3.5" /> Message Student
                          </Button>
                        </a>
                      ) : null}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}